import React from 'react';
import { useTranslation } from 'react-i18next';
import { BatteryCharging, BatteryLow, Zap, Plug, PlugZap, Gauge, Thermometer, Clock, AlertTriangle, Power } from 'lucide-react';

// Shared, self-contained UPS status card. Works with either the agent shape
// (/api/ups → last_* + latest) or the portal shape (/portal/ups → last_* + latest).
// Pure app design system (CSS variables), no Tailwind. `history` (optional) draws
// a runtime sparkline. `compact` shrinks it for grids/dashboards.

const STATUS_META = {
  onLine:        { key: 'ups.status.onLine',        color: 'var(--c-resolved, #16a34a)', icon: Plug },
  onBattery:     { key: 'ups.status.onBattery',     color: 'var(--c-pending, #d97706)', icon: PlugZap },
  onBatteryTest: { key: 'ups.status.onBatteryTest', color: 'var(--c-open, #2563eb)',    icon: Power },
  onSmartBoost:  { key: 'ups.status.boost',         color: 'var(--c-pending, #d97706)', icon: Zap },
  onSmartTrim:   { key: 'ups.status.trim',          color: 'var(--c-pending, #d97706)', icon: Zap },
  softwareBypass:{ key: 'ups.status.bypass',        color: 'var(--c-pending, #d97706)', icon: AlertTriangle },
  switchedBypass:{ key: 'ups.status.bypass',        color: 'var(--c-pending, #d97706)', icon: AlertTriangle },
  off:           { key: 'ups.status.off',           color: 'var(--c-closed, #64748b)',  icon: Power },
  unknown:       { key: 'ups.status.unknown',       color: 'var(--c-closed, #64748b)',  icon: Power },
};

function fmt(n, digits = 0) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toFixed(digits).replace(/\.0$/, '');
}

function batteryColor(pct) {
  if (pct === null || pct === undefined) return 'var(--c-closed, #64748b)';
  if (pct >= 60) return 'var(--c-resolved, #16a34a)';
  if (pct >= 30) return 'var(--c-pending, #d97706)';
  return 'var(--c-open, #dc2626)';
}

// Small inline sparkline (0..max normalized). points = array of numbers.
function Sparkline({ points, color = 'var(--c-brand)', w = 220, h = 40 }) {
  const vals = (points || []).filter((v) => v !== null && v !== undefined && !Number.isNaN(Number(v))).map(Number);
  if (vals.length < 2) return null;
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const range = max - min || 1;
  const step = w / (vals.length - 1);
  const d = vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', width: '100%' }} preserveAspectRatio="none">
      <path d={`${d} L${w},${h} L0,${h} Z`} fill={color} opacity="0.12" />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// Radial battery gauge
function BatteryRing({ pct, size = 108, stroke = 10, onBattery }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, pct ?? 0));
  const col = batteryColor(pct);
  const Icon = onBattery ? BatteryLow : BatteryCharging;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--c-border)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={c - (p / 100) * c} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset .6s ease' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={18} style={{ color: col }} />
        <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, color: 'var(--c-text)' }}>{fmt(pct)}<span style={{ fontSize: 12 }}>%</span></div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, unit, tone }) {
  return (
    <div style={{ flex: 1, minWidth: 74 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--c-text-muted)', fontSize: 11, marginBottom: 2 }}>
        {Icon && <Icon size={12} />} {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: tone || 'var(--c-text)' }}>
        {value}{unit && <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--c-text-muted)', marginLeft: 2 }}>{unit}</span>}
      </div>
    </div>
  );
}

export default function UpsCard({ ups, history, compact = false, onClick, style }) {
  const { t } = useTranslation();
  if (!ups) return null;
  const latest = ups.latest || {};
  const status = latest.status || ups.last_status || 'unknown';
  const reachable = ups.reachable !== false;
  const onBattery = status === 'onBattery' || status === 'onBatteryTest';
  const batteryPct = latest.battery_pct ?? ups.last_battery_pct;
  const runtime = latest.runtime_min ?? ups.last_runtime_min;
  const load = latest.load_pct ?? ups.last_load_pct;

  const meta = STATUS_META[status] || STATUS_META.unknown;
  const StatusIcon = !reachable ? Power : meta.icon;
  const statusColor = !reachable ? 'var(--c-open, #dc2626)' : meta.color;
  const statusLabel = !reachable ? t('ups.status.unreachable') : t(meta.key);
  const needsReplace = latest.battery_needs_replace;
  const activeAlerts = Number(ups.active_alerts || 0);

  return (
    <div
      className="card"
      onClick={onClick}
      style={{ margin: 0, padding: 16, cursor: onClick ? 'pointer' : 'default', borderLeft: `3px solid ${statusColor}`, ...style }}
    >
      {/* Header: name + status pill */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--c-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ups.name}</div>
          <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
            {[ups.model || ups.manufacturer, ups.location_name].filter(Boolean).join(' · ') || t('ups.title')}
          </div>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: statusColor, background: 'var(--c-surface-2)', border: `1px solid ${statusColor}` }}>
          <StatusIcon size={13} /> {statusLabel}
        </span>
      </div>

      {/* Body: battery ring + key stats */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <BatteryRing pct={batteryPct} onBattery={onBattery} size={compact ? 88 : 108} />
        <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <Stat icon={Clock} label={t('ups.runtime')} value={fmt(runtime)} unit="min" tone={runtime != null && runtime <= 8 ? 'var(--c-open, #dc2626)' : undefined} />
          <Stat icon={Gauge} label={t('ups.load')} value={fmt(load)} unit="%" tone={load != null && load >= 80 ? 'var(--c-pending, #d97706)' : undefined} />
          {!compact && <Stat icon={Plug} label={t('ups.input')} value={fmt(latest.input_voltage ?? ups.last_input_voltage)} unit="V" />}
          {!compact && <Stat icon={PlugZap} label={t('ups.output')} value={fmt(latest.output_voltage)} unit="V" />}
          {!compact && latest.battery_temp_c != null && <Stat icon={Thermometer} label={t('ups.temp')} value={fmt(latest.battery_temp_c)} unit="°C" tone={latest.battery_temp_c >= 45 ? 'var(--c-pending, #d97706)' : undefined} />}
        </div>
      </div>

      {/* Runtime sparkline */}
      {!compact && history && history.length > 1 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginBottom: 2 }}>{t('ups.runtimeTrend')}</div>
          <Sparkline points={history.map((h) => h.runtime_min)} color={statusColor} />
        </div>
      )}

      {/* Flags row */}
      {(needsReplace || activeAlerts > 0 || onBattery) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
          {onBattery && <span style={badge('var(--c-pending, #d97706)')}><PlugZap size={12} /> {t('ups.flag.onBattery')}</span>}
          {needsReplace && <span style={badge('var(--c-open, #dc2626)')}><AlertTriangle size={12} /> {t('ups.flag.replaceBattery')}</span>}
          {activeAlerts > 0 && <span style={badge('var(--c-open, #dc2626)')}><AlertTriangle size={12} /> {t('ups.flag.activeAlerts', { count: activeAlerts })}</span>}
        </div>
      )}
    </div>
  );
}

function badge(color) {
  return { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, color, background: 'var(--c-surface-2)', border: `1px solid ${color}` };
}
